/**
 * What to call a child on screen.
 *
 * THE RULE, in order: the preferred name the family gave, else the first name, else
 * whatever name-ish text the caller has. Then masked, exactly as before.
 *
 * WHY A PREFERRED NAME AT ALL. A child's legal name is the most identifying thing on a
 * clinical screen, which is why maskChildName exists. A preferred name is the better
 * answer to the same problem: it is what the team already calls the child, it is short,
 * and it is not the legal identifier. It also survives masking far better — the mask
 * keeps the first and last character of each word, so "Chai" reads as "C**i" and
 * anything of two characters or fewer is left whole, whereas a masked full legal name
 * ("C****a K***r") is nearly unreadable.
 *
 * MASKING FOLLOWS THE SOURCE OF THE NAME, which is the whole reason the two are worth
 * telling apart:
 *
 *   • a PREFERRED name is shown as given. It is what the team already says out loud in
 *     the room, it is not the legal identifier, and starring it out would defeat the
 *     point of collecting it.
 *   • a LEGAL name is masked, exactly as before. The fallback is not a name the family
 *     chose to publish — it is the child's real first name standing in because nobody
 *     filled the field — so it keeps the protection it has always had. See the note at
 *     the top of maskChildName for why that protection exists.
 *
 * So a grid can show "Chai" next to "C****a", and that is correct rather than
 * inconsistent: the first family offered a name for daily use and the second did not.
 * Passing `mask` explicitly overrides the rule in either direction.
 *
 * THE FALLBACK IS NEVER STORED. The server returns preferred_name as null when the
 * family did not give one rather than defaulting it to the first name, so "does this
 * child have a preferred name" stays answerable by the edit form and by exports. The
 * substitution happens here, at the point of display, and only here.
 */
import { maskChildName } from './maskChildName';

/**
 * Pull the best available name out of whatever shape the caller holds.
 * Call sites carry clients, persons, enquiry rows and bare strings; rather than make
 * each one normalise first, this accepts all of them.
 */
const rawName = (child) => {
  if (!child) return '';
  if (typeof child === 'string') return child.trim();

  const preferred = (child.preferred_name || child.preferredName || '').trim();
  if (preferred) return preferred;

  const first = (child.first_name || child.firstName || '').trim();
  if (first) return first;

  // display_name is the DERIVED "first last" every create path writes, so it is the
  // last resort rather than a preference — it is never the family's choice.
  const full = (child.display_name || child.displayName || child.name || child.child_name || '').trim();
  return full;
};

/**
 * The on-screen name for a child.
 *
 * @param {object|string} child   a client/person object, or a plain name string
 * @param {object}  [options]
 * @param {boolean} [options.mask]        force masking on or off. Omit for the rule
 *                                        above: preferred names in the clear, legal
 *                                        names masked.
 * @param {string}  [options.legalName]   what to show when there is no preferred name.
 *                                        Callers that already render a particular legal
 *                                        form — "First Last" on a card, say — pass it
 *                                        here so adding a preferred name never SHORTENS
 *                                        what a child without one displays. Masked, as
 *                                        a legal name always is.
 * @param {string}  [options.fallback=''] shown when there is no name at all
 * @returns {string}
 */
export function childDisplayName(child, { mask, legalName, fallback = '' } = {}) {
  const preferred = preferredNameOf(child);
  const name = preferred || (legalName || '').trim() || rawName(child);
  if (!name) return fallback;
  // A bare string carries no provenance — there is no way to know whether it is a
  // preferred name or a legal one, so it is masked unless the caller says otherwise.
  const shouldMask = mask === undefined ? !preferred : mask;
  return shouldMask ? maskChildName(name) : name;
}

/** The family-supplied preferred name, or '' — the single place that question is asked. */
function preferredNameOf(child) {
  if (!child || typeof child === 'string') return '';
  return (child.preferred_name || child.preferredName || '').trim();
}

/** Did the family actually give a preferred name? Distinct from what is displayed. */
export function hasPreferredName(child) {
  return Boolean(preferredNameOf(child));
}

export default childDisplayName;
